import React, { useCallback, useRef, useState } from 'react';

import PropTypes from 'prop-types';
import { FaQuestionCircle } from 'react-icons/fa';
import styled, { css } from 'styled-components';

import { Mobile, Desktop } from '@techstyle/react-components-legacy';
import { useIntl } from '@techstyle/react-intl';

import { mobile } from '../../styles';
import { Header } from '../../styles/header';
import { BasicModal } from '../../styles/modal';
import { Component as StaticIcon } from '../StaticIcon';

const CONSTANTS_PX = {
  caretWidth: 5,
  defaultToolTipBoxWidth: 200,
};

const dialogStyle = css`
  margin: ${props => props.theme.spacing.small}px;
  padding: 26px;
  font-size: 12px;
  line-height: 18px;
  letter-spacing: 0.38px;
`;

const StyledModal = styled(BasicModal)``;

const ModalCloseButton = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  padding: 12px;
  cursor: pointer;
`;

const CloseIcon = styled(StaticIcon).attrs(props => ({
  type: 'close',
  size: 8,
  color: props.theme.colors.gray250,
}))`
  stroke: ${props => props.theme.colors.gray250};
  stroke-width: 1.5px;
`;

const ModalHeader = styled(Header).attrs({
  level: 6,
})`
  font-size: 16px;
  text-align: center;
  text-transform: uppercase;
  font-weight: 700;
  margin-bottom: 8px;
`;

const Wrapper = styled.span`
  display: inline-block;
  cursor: pointer;
  position: relative;
`;

const TriggerWrapper = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: normal;
  font-size: 10px;
  margin-left: 3px;
  width: 10px;
  line-height: 1.8;
  cursor: pointer;

  ${mobile`
    font-size: 12px;
    width: 40px;
    height: 40px;
    left: -9px;
    position: relative;
    top: 0px;
  `}
`;

const ToolTipBox = styled.span`
  display: block;
  position: absolute;
  top: 8px;
  left: ${props => props.toolTipBoxStyle.boxLeft}px;
  width: ${props => props.toolTipBoxStyle.width}px;
  padding: ${props => props.theme.spacing.small}px;
  background-color: ${props => props.theme.colors.default};
  font-size: 12px;
  font-weight: normal;
  font-style: normal;
  font-stretch: normal;
  line-height: 1.5;
  letter-spacing: 0.7px;
  text-align: left;
  color: ${props => props.theme.colors.white};
  opacity: 0;
  visibility: hidden;
  transition: 0.25s opacity ease-in-out, 0.25s visibility ease-in-out;

  &:after {
    content: '';
    position: absolute;
    width: 0;
    height: 0;
    border-width: ${CONSTANTS_PX.caretWidth}px;
    border-style: solid;
    border-color: transparent transparent ${props => props.theme.colors.default}
      transparent;
    top: -10px;
    left: ${props => props.toolTipBoxStyle.caretLeft}px;
  }

  ${Wrapper}:hover & {
    transition: 0.25s opacity ease-in-out, 0.25s visibility ease-in-out;
    opacity: 1;
    visibility: visible;
  }

  ${props =>
    props.isFocused &&
    `
    opacity: 1;
    visibility: visible;
  `}
`;

function QuestionMarkToolTip({
  anchorToRef,
  children,
  icon,
  mobileTitle,
  className,
}) {
  const [isFocused, setIsFocused] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isToolTipBoxPositionSet, setIsToolTipBoxPositionSet] = useState(false);
  const [toolTipBoxStyle, setToolTipBoxStyle] = useState({
    boxLeft: 0,
    caretLeft: 0,
    width: CONSTANTS_PX.defaultToolTipBoxWidth,
  });
  const toolTipBoxRef = useRef();
  const questionMarkRef = useRef();
  const intl = useIntl();

  const positionToolTipBox = useCallback(() => {
    if (!toolTipBoxRef.current || !questionMarkRef.current) {
      return;
    }

    // Position the tooltip hover box to align exactly with anchorToRef
    if (anchorToRef.current) {
      const { right: anchorRight, left: anchorLeft } =
        anchorToRef.current.getBoundingClientRect();
      const { left: questionMarkLeft } =
        questionMarkRef.current.getBoundingClientRect();
      const { left: toolTipBoxLeft } =
        toolTipBoxRef.current.getBoundingClientRect();
      return setToolTipBoxStyle({
        boxLeft: anchorLeft - toolTipBoxLeft,
        caretLeft: questionMarkLeft - anchorLeft,
        width: anchorRight - anchorLeft,
      });
    }

    // If no anchorToRef, position the tooltip caret to the middle of questionMarkRef
    const { width } = questionMarkRef.current.getBoundingClientRect();
    setToolTipBoxStyle({
      ...toolTipBoxStyle,
      caretLeft: Math.round(width / 2 - CONSTANTS_PX.caretWidth / 2),
    });
  }, [anchorToRef, toolTipBoxStyle]);

  const handleMouseEnter = useCallback(() => {
    if (!isToolTipBoxPositionSet) {
      positionToolTipBox();
      setIsToolTipBoxPositionSet(true);
    }
  }, [isToolTipBoxPositionSet, positionToolTipBox]);
  const handleMobileOpen = useCallback(e => {
    e.preventDefault();
    setIsModalOpen(true);
  }, []);
  const handleMobileClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);
  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);
  const handleBlur = useCallback(() => {
    setIsFocused(false);
  }, []);
  const preventDefault = useCallback(e => {
    e.preventDefault();
  }, []);

  return (
    <>
      <Desktop>
        <Wrapper className={className} onMouseEnter={handleMouseEnter}>
          <TriggerWrapper
            ref={questionMarkRef}
            onFocus={handleFocus}
            onClick={preventDefault}
            onBlur={handleBlur}
            role="button"
            aria-label={intl.formatMessage({
              id: 'global_cta.question_mark_tool_tip_aria',
              defaultMessage: 'more info',
            })}
          >
            {icon}
          </TriggerWrapper>
          <ToolTipBox
            toolTipBoxStyle={toolTipBoxStyle}
            isFocused={isFocused}
            ref={toolTipBoxRef}
            role="tooltip"
          >
            {children}
          </ToolTipBox>
        </Wrapper>
      </Desktop>

      <Mobile>
        <Wrapper className={className}>
          <TriggerWrapper
            aria-label={intl.formatMessage({
              id: 'global_cta.question_mark_tool_tip_aria',
              defaultMessage: 'more info',
            })}
            onClick={handleMobileOpen}
          >
            {icon}
          </TriggerWrapper>
        </Wrapper>
        <StyledModal
          closeButton={
            <ModalCloseButton>
              <CloseIcon />
            </ModalCloseButton>
          }
          isOpen={isModalOpen}
          onExit={handleMobileClose}
          dialogStyle={dialogStyle}
          titleId="questionmark-modal"
        >
          <ModalHeader id="questionmark-modal">{mobileTitle}</ModalHeader>
          {children}
        </StyledModal>
      </Mobile>
    </>
  );
}

QuestionMarkToolTip.ToolTipBox = ToolTipBox;
QuestionMarkToolTip.TriggerWrapper = TriggerWrapper;

QuestionMarkToolTip.defaultProps = {
  anchorToRef: { current: null },
  children: '',
  icon: <FaQuestionCircle />,
  mobileTitle: '',
  className: '',
};

QuestionMarkToolTip.propTypes = {
  /**
   * Ref for parent wrapper to position hover tooltip around.
   */
  anchorToRef: PropTypes.oneOfType([
    PropTypes.shape({ current: PropTypes.instanceOf(PropTypes.Element) }),
  ]),
  /**
   * The message to be displayed inside the tooltip.
   */
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  className: PropTypes.string,
  /**
   * The icon to be used for the tooltip trigger.
   */
  icon: PropTypes.element,
  /**
   * The title for the mobile modal.
   */
  mobileTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
};

export default QuestionMarkToolTip;
